import {
  RichTextMarkdownArtifactCanvas,
  useMarkdownArtifactDraft,
} from '@/components/artifact/rich-text-markdown-artifact-canvas'
import { MARKDOWN_FIELD_MAX_LENGTH } from '@/constants'
import {
  useUpdateSimulationJob,
  type SimulationScenario,
} from '@/hooks/use-platform-simulation'
import { markdownFieldLengthError } from '@/lib/rich-text-visible-char-count'

type DataBenchJobDescriptionCanvasProps = {
  scenarioId: string
  title: string
  description: string
  onClose: () => void
  onSaved?: (updated: SimulationScenario) => void
}

/** Sample job description — saves through Simulation, not employer publish. */
export function DataBenchJobDescriptionCanvas({
  scenarioId,
  title,
  description,
  onClose,
  onSaved,
}: DataBenchJobDescriptionCanvasProps) {
  const updateJob = useUpdateSimulationJob()
  const draft = useMarkdownArtifactDraft(description, scenarioId)

  const handleSave = () => {
    if (draft.charCount > MARKDOWN_FIELD_MAX_LENGTH) {
      draft.setValidationError(
        `Job description must be at most ${MARKDOWN_FIELD_MAX_LENGTH} characters.`,
      )
      return
    }

    if (!draft.value.trim()) {
      draft.setValidationError('Job description is required')
      return
    }

    const descriptionLengthErr = markdownFieldLengthError(draft.value, 'Job description')
    if (descriptionLengthErr) {
      draft.setValidationError(descriptionLengthErr)
      return
    }

    draft.setValidationError(null)

    void updateJob
      .mutateAsync({
        scenarioId,
        title: title.trim() || undefined,
        description: draft.value.trim(),
      })
      .then((updated) => {
        onSaved?.(updated)
        onClose()
      })
      .catch(() => {
        // mutation error handled by hook consumer if needed
      })
  }

  const serverError =
    updateJob.error instanceof Error ? updateJob.error.message : updateJob.error ? String(updateJob.error) : null

  return (
    <RichTextMarkdownArtifactCanvas
      id="artifact-sim-job-description"
      editorChrome="document"
      value={draft.value}
      onChange={draft.onChange}
      charCount={draft.charCount}
      onCharCountChange={draft.onCharCountChange}
      validationError={draft.validationError}
      serverError={serverError}
      onClose={onClose}
      onSave={handleSave}
      isDirty={draft.isDirty}
      isSaving={updateJob.isPending}
    />
  )
}
